// @givmo/auth — WebAuthn JSON ⇄ browser-object codec (AS-WEB2).
//
// The backend speaks the W3C **JSON** forms of the WebAuthn types (its py_webauthn `options_to_json`
// emits `PublicKeyCredentialRequestOptionsJSON`, and its parser consumes `AuthenticationResponseJSON`),
// while `navigator.credentials.get()` speaks the browser forms, whose `challenge` / credential ids /
// signatures are `ArrayBuffer`s. Every byte-string crosses that boundary as **base64url** (RFC 4648
// §5), so the whole of this module is that one translation — kept apart from `passkeys.ts` so it can
// be tested without a `CredentialsContainer`, a Firebase `Auth`, or a transport.
//
// Both directions PREFER the browser's own converters (`PublicKeyCredential.parseRequestOptionsFromJSON`
// / `credential.toJSON()`) and fall back to an explicit shim. That order is not just about older
// browsers: the native converters understand fields this module has never heard of, so a backend that
// starts emitting one (a new extension, a new hint) keeps working on a current browser instead of
// having it silently dropped by our field-by-field copy. The shim is what keeps a browser that can do
// the CEREMONY but not the CONVERSION (WebAuthn shipped years before the JSON helpers did) from being
// excluded — it maps the fields the Givmo backend actually emits, and passes `extensions` through
// untouched.
//
// Nothing here logs. The assertion carries a signature over a server challenge, not a secret, but the
// same discipline as the rest of the package applies: values pass through, they are never printed.

/** The base64url alphabet, RFC 4648 §5 — the standard alphabet with `+`→`-` and `/`→`_`. */
const BASE64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/**
 * Encode bytes as unpadded base64url (RFC 4648 §5). Unpadded because that is what the WebAuthn JSON
 * types specify and what py_webauthn emits and accepts.
 *
 * Implemented over the alphabet directly rather than `btoa` + character replacement: `btoa` throws on
 * any code unit above 0xFF, so the intermediate binary string it needs is one more place a caller
 * could get the byte handling subtly wrong for nothing in return. The loop walks the whole buffer in
 * one pass — it appends per character and never spreads, so there is no call-stack bound to work
 * around, and a chunked variant would only add a seam for a 3-byte group to straddle.
 */
export function base64UrlEncode(input: ArrayBuffer | ArrayBufferView): string {
  // ⚠ BRAND CHECK, NOT `instanceof`, AND THE DIFFERENCE IS A SILENT WRONG ANSWER.
  //
  // `instanceof` compares against THIS realm's `ArrayBuffer`, so a buffer minted in another one —
  // Node's `webcrypto` under jsdom, an iframe, a worker — fails `input instanceof ArrayBuffer` even
  // though it is one. The old form then took the view branch and read `.buffer` / `.byteOffset` off
  // an object that has neither: `new Uint8Array(undefined, undefined, n)` yields an EMPTY array, so
  // the function returned `''`. It did not throw. Every caller downstream then encodes nothing and
  // discovers it as a server-side rejection with no local explanation — a WebAuthn assertion that
  // fails to verify, or (the case that found this) a PKCE `code_challenge` the backend 422s.
  //
  // `ArrayBuffer.isView` asks the internal slot instead of the realm, so it is correct across all of
  // them. The two branches are byte-identical for same-realm inputs, which is what keeps the passkey
  // paths — the only production callers today — unchanged.
  //
  // ★ A ROUND TRIP THROUGH THIS FUNCTION CANNOT CATCH THE REGRESSION: encode-then-decode compares
  // empty to empty and passes. `webauthn.test.ts` asserts external RFC 4648 §10 vectors and a
  // foreign-realm buffer for exactly that reason.
  const bytes = ArrayBuffer.isView(input)
    ? new Uint8Array(input.buffer, input.byteOffset, input.byteLength)
    : new Uint8Array(input);

  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i] as number;
    const b1 = bytes[i + 1];
    const b2 = bytes[i + 2];
    out += BASE64URL_ALPHABET[b0 >> 2];
    out += BASE64URL_ALPHABET[((b0 & 0x03) << 4) | ((b1 ?? 0) >> 4)];
    if (b1 === undefined) break;
    out += BASE64URL_ALPHABET[((b1 & 0x0f) << 2) | ((b2 ?? 0) >> 6)];
    if (b2 === undefined) break;
    out += BASE64URL_ALPHABET[b2 & 0x3f];
  }
  return out;
}

/**
 * Decode base64url (RFC 4648 §5) to bytes. Tolerant on the way in — `=` padding is optional, and the
 * standard alphabet's `+`/`/` are accepted alongside `-`/`_` — because this reads a value produced
 * elsewhere. Throws {@link RangeError} on a character outside both alphabets or a truncated group,
 * rather than returning silently-wrong bytes that would fail later as an opaque signature mismatch.
 */
export function base64UrlDecode(value: string): Uint8Array<ArrayBuffer> {
  const normalized = value.replace(/[-_]/g, (c) => (c === '-' ? '+' : '/')).replace(/=+$/, '');
  const remainder = normalized.length % 4;
  if (remainder === 1) {
    throw new RangeError('Invalid base64url: truncated group.');
  }

  const bytes = new Uint8Array(Math.floor((normalized.length * 3) / 4));
  let accumulator = 0;
  let bits = 0;
  let written = 0;
  for (const char of normalized) {
    const sextet = BASE64URL_ALPHABET.indexOf(char === '+' ? '-' : char === '/' ? '_' : char);
    if (sextet < 0) {
      throw new RangeError('Invalid base64url: unexpected character.');
    }
    accumulator = (accumulator << 6) | sextet;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes[written++] = (accumulator >> bits) & 0xff;
    }
  }
  return bytes.subarray(0, written);
}

/** One entry of the JSON `allowCredentials` list (`PublicKeyCredentialDescriptorJSON`). */
interface CredentialDescriptorJson {
  id?: unknown;
  type?: unknown;
  transports?: unknown;
}

/**
 * The subset of `PublicKeyCredentialRequestOptionsJSON` the Givmo backend emits. Everything is
 * optional here except `challenge`, because a tolerant reader must not assume a field it happens not
 * to need. `allowCredentials` is absent by design on this backend — the assertion route issues options
 * for DISCOVERABLE credentials so the response cannot be used to enumerate which accounts exist.
 */
interface RequestOptionsJson {
  challenge?: unknown;
  timeout?: unknown;
  rpId?: unknown;
  allowCredentials?: unknown;
  userVerification?: unknown;
  extensions?: unknown;
}

/**
 * The global surface this module probes for the browser's own JSON converters. Both members are
 * optional so `globalThis` satisfies it on a browser that has them and a test double satisfies it
 * with neither — and so the shim branch stays reachable, which is the branch older browsers take.
 */
export interface WebAuthnScope {
  PublicKeyCredential?: {
    parseRequestOptionsFromJSON?: (
      json: PublicKeyCredentialRequestOptionsJSON,
    ) => PublicKeyCredentialRequestOptions;
    parseCreationOptionsFromJSON?: (
      json: PublicKeyCredentialCreationOptionsJSON,
    ) => PublicKeyCredentialCreationOptions;
  };
}

/**
 * Whether this environment can run a WebAuthn ceremony at all. The gate every passkey affordance sits
 * behind: no `PublicKeyCredential` means no `navigator.credentials.get({publicKey})`, so a button
 * would be a dead end. Deliberately checks only the constructor — the JSON converters are optional
 * (the shim covers their absence) and requiring them would exclude browsers that can do the ceremony.
 */
export function isWebAuthnSupported(scope: WebAuthnScope = globalThis): boolean {
  return typeof scope.PublicKeyCredential === 'function';
}

/**
 * Convert the backend's assertion-options JSON into the object `navigator.credentials.get()` wants.
 *
 * Prefers the browser's `PublicKeyCredential.parseRequestOptionsFromJSON` (see the module header for
 * why), and falls back to the field-by-field shim. Throws {@link TypeError} when the document carries
 * no string `challenge`: that is the one field the ceremony cannot proceed without, and passing an
 * `undefined` challenge to the browser fails as an opaque `NotSupportedError` well away from the
 * cause.
 */
export function toRequestOptions(
  json: unknown,
  scope: WebAuthnScope = globalThis,
): PublicKeyCredentialRequestOptions {
  if (typeof json !== 'object' || json === null) {
    throw new TypeError('Assertion options were not an object.');
  }

  const native = scope.PublicKeyCredential?.parseRequestOptionsFromJSON;
  if (typeof native === 'function') {
    // The cast is the boundary: the document came off the wire as `unknown`, and validating it
    // field-by-field first would defeat the reason for preferring the native converter — it
    // understands fields this module does not. It throws on a document it cannot read, which is
    // exactly the outcome the shim reaches by its own checks below.
    return native.call(scope.PublicKeyCredential, json as PublicKeyCredentialRequestOptionsJSON);
  }

  const doc = json as RequestOptionsJson;
  if (typeof doc.challenge !== 'string' || doc.challenge.length === 0) {
    throw new TypeError('Assertion options carried no challenge.');
  }

  const options: PublicKeyCredentialRequestOptions = { challenge: base64UrlDecode(doc.challenge) };
  if (typeof doc.timeout === 'number') options.timeout = doc.timeout;
  if (typeof doc.rpId === 'string') options.rpId = doc.rpId;
  if (typeof doc.userVerification === 'string') {
    options.userVerification = doc.userVerification as UserVerificationRequirement;
  }
  if (Array.isArray(doc.allowCredentials)) {
    options.allowCredentials = doc.allowCredentials.flatMap(toCredentialDescriptor);
  }
  // Extensions ride through untouched: their JSON form is already JSON-safe for every extension this
  // surface uses. An extension whose JSON form carries base64url (e.g. `prf`'s eval inputs) would need
  // the native converter — which is exactly the browser generation that has it.
  if (typeof doc.extensions === 'object' && doc.extensions !== null) {
    options.extensions = doc.extensions as AuthenticationExtensionsClientInputs;
  }
  return options;
}

/**
 * Map one JSON credential descriptor onto its browser form. Returns `[]` for a malformed entry so a
 * single bad row narrows the allow-list rather than failing the whole ceremony — the tolerant-reader
 * posture the rest of the repo's decodes take.
 */
function toCredentialDescriptor(raw: unknown): PublicKeyCredentialDescriptor[] {
  if (typeof raw !== 'object' || raw === null) return [];
  const entry = raw as CredentialDescriptorJson;
  if (typeof entry.id !== 'string' || entry.id.length === 0) return [];
  const descriptor: PublicKeyCredentialDescriptor = {
    id: base64UrlDecode(entry.id),
    type: 'public-key',
  };
  if (Array.isArray(entry.transports)) {
    // The browser ignores a transport hint it does not recognise, so passing the strings through is
    // forward-compatible; the cast only satisfies the lib's closed union.
    descriptor.transports = entry.transports.filter(
      (t): t is AuthenticatorTransport => typeof t === 'string',
    );
  }
  return [descriptor];
}

/**
 * The `AuthenticationResponseJSON` shape the backend's parser consumes. Modelled here rather than
 * imported because the DOM lib's coverage of the WebAuthn JSON types varies by TypeScript version,
 * and this shape is a wire contract either way.
 */
export interface PasskeyAssertionJson {
  id: string;
  rawId: string;
  type: string;
  authenticatorAttachment?: string | null;
  clientExtensionResults: Record<string, unknown>;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle: string | null;
  };
}

/**
 * The structural shape {@link serializeAssertion} reads off a credential. Written out rather than
 * using `PublicKeyCredential` so the module compiles against any DOM lib vintage (`toJSON` in
 * particular is a recent addition) and so a fake in a test is an ordinary object literal.
 */
export interface AssertionCredentialLike {
  id: string;
  rawId: ArrayBuffer;
  type: string;
  authenticatorAttachment?: string | null;
  response: {
    clientDataJSON: ArrayBuffer;
    authenticatorData: ArrayBuffer;
    signature: ArrayBuffer;
    userHandle?: ArrayBuffer | null;
  };
  getClientExtensionResults?: () => Record<string, unknown>;
  toJSON?: () => unknown;
}

/**
 * Serialize an assertion credential into the JSON the backend parses.
 *
 * Prefers the credential's own `toJSON()`; the shim below is byte-equivalent for the fields this
 * ceremony produces. A `userHandle` of `null` is emitted explicitly rather than omitted — the
 * discoverable-credential flow is the one that carries it, and it is what tells the backend which
 * account asserted.
 */
export function serializeAssertion(credential: AssertionCredentialLike): PasskeyAssertionJson {
  if (typeof credential.toJSON === 'function') {
    const native = credential.toJSON();
    if (typeof native === 'object' && native !== null) {
      return native as PasskeyAssertionJson;
    }
  }

  const userHandle = credential.response.userHandle;
  return {
    id: credential.id,
    rawId: base64UrlEncode(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? null,
    clientExtensionResults: credential.getClientExtensionResults?.() ?? {},
    response: {
      clientDataJSON: base64UrlEncode(credential.response.clientDataJSON),
      authenticatorData: base64UrlEncode(credential.response.authenticatorData),
      signature: base64UrlEncode(credential.response.signature),
      userHandle:
        userHandle === undefined || userHandle === null ? null : base64UrlEncode(userHandle),
    },
  };
}

// ── REGISTRATION (AS-WEB11): the creation options in, the attestation out ───────────────────────────
//
// The same two-way discipline as the assertion above: prefer the browser's own converters
// (`PublicKeyCredential.parseCreationOptionsFromJSON` / `credential.toJSON()`), fall back to a shim that
// maps exactly the fields py_webauthn's `options_to_json` emits for a registration and its
// `parse_registration_credential_json` reads back.

/** The subset of `PublicKeyCredentialCreationOptionsJSON` the Givmo backend emits. */
interface CreationOptionsJson {
  rp?: unknown;
  user?: unknown;
  challenge?: unknown;
  pubKeyCredParams?: unknown;
  timeout?: unknown;
  excludeCredentials?: unknown;
  authenticatorSelection?: unknown;
  attestation?: unknown;
  extensions?: unknown;
}

/**
 * Convert the backend's registration-options JSON into the object `navigator.credentials.create()`
 * wants. Throws {@link TypeError} when the document carries no string `challenge` or no `user.id` —
 * the two byte fields the ceremony cannot run without.
 */
export function toCreationOptions(
  json: unknown,
  scope: WebAuthnScope = globalThis,
): PublicKeyCredentialCreationOptions {
  if (typeof json !== 'object' || json === null) {
    throw new TypeError('Registration options were not an object.');
  }

  const native = scope.PublicKeyCredential?.parseCreationOptionsFromJSON;
  if (typeof native === 'function') {
    return native.call(scope.PublicKeyCredential, json as PublicKeyCredentialCreationOptionsJSON);
  }

  const doc = json as CreationOptionsJson;
  if (typeof doc.challenge !== 'string' || doc.challenge.length === 0) {
    throw new TypeError('Registration options carried no challenge.');
  }
  const user = (typeof doc.user === 'object' && doc.user !== null ? doc.user : {}) as {
    id?: unknown;
    name?: unknown;
    displayName?: unknown;
  };
  if (typeof user.id !== 'string' || user.id.length === 0) {
    throw new TypeError('Registration options carried no user id.');
  }
  const rp = (typeof doc.rp === 'object' && doc.rp !== null ? doc.rp : {}) as {
    id?: unknown;
    name?: unknown;
  };

  const options: PublicKeyCredentialCreationOptions = {
    challenge: base64UrlDecode(doc.challenge),
    rp: {
      name: typeof rp.name === 'string' ? rp.name : '',
      ...(typeof rp.id === 'string' ? { id: rp.id } : {}),
    },
    user: {
      id: base64UrlDecode(user.id),
      name: typeof user.name === 'string' ? user.name : '',
      displayName: typeof user.displayName === 'string' ? user.displayName : '',
    },
    pubKeyCredParams: Array.isArray(doc.pubKeyCredParams)
      ? doc.pubKeyCredParams.flatMap((param: unknown) => {
          const entry = param as { type?: unknown; alg?: unknown } | null;
          return entry !== null && typeof entry.alg === 'number'
            ? [{ type: 'public-key' as const, alg: entry.alg }]
            : [];
        })
      : [],
  };
  if (typeof doc.timeout === 'number') options.timeout = doc.timeout;
  if (Array.isArray(doc.excludeCredentials)) {
    options.excludeCredentials = doc.excludeCredentials.flatMap(toCredentialDescriptor);
  }
  if (typeof doc.authenticatorSelection === 'object' && doc.authenticatorSelection !== null) {
    options.authenticatorSelection = doc.authenticatorSelection as AuthenticatorSelectionCriteria;
  }
  if (typeof doc.attestation === 'string') {
    options.attestation = doc.attestation as AttestationConveyancePreference;
  }
  if (typeof doc.extensions === 'object' && doc.extensions !== null) {
    options.extensions = doc.extensions as AuthenticationExtensionsClientInputs;
  }
  return options;
}

/** The `RegistrationResponseJSON` shape the backend's parser consumes. */
export interface PasskeyAttestationJson {
  id: string;
  rawId: string;
  type: string;
  authenticatorAttachment?: string | null;
  clientExtensionResults: Record<string, unknown>;
  response: {
    clientDataJSON: string;
    attestationObject: string;
    transports?: string[];
  };
}

/** The structural shape {@link serializeAttestation} reads off a created credential. */
export interface AttestationCredentialLike {
  id: string;
  rawId: ArrayBuffer;
  type: string;
  authenticatorAttachment?: string | null;
  response: {
    clientDataJSON: ArrayBuffer;
    attestationObject: ArrayBuffer;
    getTransports?: () => string[];
  };
  getClientExtensionResults?: () => Record<string, unknown>;
  toJSON?: () => unknown;
}

/**
 * Serialize a created credential into the JSON the backend parses. Prefers the credential's own
 * `toJSON()`; the shim carries the transports when the browser reports them, because the backend
 * stores them and later hands them back in `allowCredentials`, which is what lets a phone or a
 * security key be offered for the account's own re-confirmation.
 */
export function serializeAttestation(
  credential: AttestationCredentialLike,
): PasskeyAttestationJson {
  if (typeof credential.toJSON === 'function') {
    const native = credential.toJSON();
    if (typeof native === 'object' && native !== null) {
      return native as PasskeyAttestationJson;
    }
  }
  const transports = credential.response.getTransports?.();
  return {
    id: credential.id,
    rawId: base64UrlEncode(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment ?? null,
    clientExtensionResults: credential.getClientExtensionResults?.() ?? {},
    response: {
      clientDataJSON: base64UrlEncode(credential.response.clientDataJSON),
      attestationObject: base64UrlEncode(credential.response.attestationObject),
      ...(Array.isArray(transports) ? { transports } : {}),
    },
  };
}
